import React, { useEffect, useMemo, useState } from "react";
import { Trash2, Plus, Check, Loader2, Type } from "lucide-react";
import TrainingReviewCard from "./TrainingReviewCard";
import TrainingSuggestionCard from "./TrainingSuggestionCard";

type IdentityTabProps = {
  guidelines: BrandGuidelines;
  onUpdate: (patch: Partial<BrandGuidelines>) => void;
  saveStatus: "idle" | "saving" | "saved";
  trainableAssetCount: number;
  isTraining: boolean;
  pendingTraining: TrainingResponse | null;
  onTrainClick: () => void;
  onDiscardTraining: () => void;
};

const STRUCTURED_FIELDS: Array<{
  key: "voice_and_tone" | "photography_style" | "lighting_rules" | "composition_rules";
  label: string;
  placeholder: string;
}> = [
  {
    key: "voice_and_tone",
    label: "Voice & tone",
    placeholder: "e.g. confident, warm, energetic",
  },
  {
    key: "photography_style",
    label: "Photography style",
    placeholder: "e.g. editorial, cinematic shallow depth of field",
  },
  {
    key: "lighting_rules",
    label: "Lighting rules",
    placeholder: "e.g. soft directional light from top-left, warm tone",
  },
  {
    key: "composition_rules",
    label: "Composition rules",
    placeholder: "e.g. rule of thirds, product centered, 20% negative space",
  },
];

const IdentityTab: React.FC<IdentityTabProps> = ({
  guidelines,
  onUpdate,
  saveStatus,
  trainableAssetCount,
  isTraining,
  pendingTraining,
  onTrainClick,
  onDiscardTraining,
}) => {
  const addColor = () => onUpdate({ colors: [...guidelines.colors, "#000000"] });
  const removeColor = (idx: number) =>
    onUpdate({ colors: guidelines.colors.filter((_, i) => i !== idx) });
  const updateColor = (idx: number, value: string) =>
    onUpdate({ colors: guidelines.colors.map((c, i) => (i === idx ? value : c)) });

  const addFont = () => onUpdate({ fonts: [...guidelines.fonts, ""] });
  const removeFont = (idx: number) =>
    onUpdate({ fonts: guidelines.fonts.filter((_, i) => i !== idx) });
  const updateFont = (idx: number, value: string) =>
    onUpdate({ fonts: guidelines.fonts.map((f, i) => (i === idx ? value : f)) });

  const doNotList = guidelines.do_not_list ?? [];
  const addDoNot = () => onUpdate({ do_not_list: [...doNotList, ""] });
  const removeDoNot = (idx: number) =>
    onUpdate({ do_not_list: doNotList.filter((_, i) => i !== idx) });
  const updateDoNot = (idx: number, value: string) =>
    onUpdate({ do_not_list: doNotList.map((d, i) => (i === idx ? value : d)) });

  const textConv = guidelines.text_content_conventions;

  const formatTextConventions = (conv: TextContentConventions | undefined): string => {
    if (!conv) return "";
    const lines: string[] = [];
    lines.push(conv.ads_typically_contain_text ? "Ads typically contain text overlays." : "Ads do not typically contain text.");
    if (conv.summary) lines.push(conv.summary);
    for (const el of conv.typical_text_elements ?? []) {
      lines.push(`- ${el.role} (${el.placement}): ${el.description}`);
    }
    return lines.join("\n");
  };

  // Inverse of formatTextConventions — parses the editable textarea back into
  // the structured shape. Falls back to `fallback` for anything the user
  // didn't change (so an unedited Save round-trips cleanly).
  const parseTextConventions = (
    value: string,
    fallback: TextContentConventions,
  ): TextContentConventions => {
    const lines = value.split("\n").map((l) => l.trim());
    const firstLine = lines.find((l) => l.length > 0) ?? "";
    let ads_typically_contain_text = fallback.ads_typically_contain_text;
    if (/^ads typically contain text/i.test(firstLine)) ads_typically_contain_text = true;
    else if (/^ads do not typically contain text/i.test(firstLine)) ads_typically_contain_text = false;

    const bulletLines = lines.filter((l) => /^-\s+/.test(l));
    const nonBulletAfterHeader = lines
      .filter((l) => l && !/^-\s+/.test(l) && l !== firstLine);
    const summary = nonBulletAfterHeader.join(" ").trim() || fallback.summary;

    const typical_text_elements = bulletLines.map((l) => {
      // Expected format: "- role (placement): description"
      const m = l.match(/^-\s*([^(]+?)\s*\(([^)]*)\)\s*:\s*(.*)$/);
      if (m) return { role: m[1].trim(), placement: m[2].trim(), description: m[3].trim() };
      // Degrade gracefully: whole bullet becomes the description.
      return { role: "", placement: "", description: l.replace(/^-\s*/, "") };
    });

    return {
      ads_typically_contain_text,
      summary,
      typical_text_elements: typical_text_elements.length > 0
        ? typical_text_elements
        : fallback.typical_text_elements,
    };
  };

  const currentTextConvFormatted = useMemo(() => formatTextConventions(textConv), [textConv]);

  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  useEffect(() => {
    setDismissed(new Set());
  }, [pendingTraining]);

  return (
    <div className="space-y-6">
      <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-100 mb-1">Train brand rules</h3>
          <p className="text-sm text-gray-400 mb-1">
            Analyzes your reference images to refine brand rules based on what it actually sees — colors, lighting, composition, and style.
          </p>
          <p className="text-xs text-gray-500">
            {guidelines.training_last_run_at
              ? `Last trained: ${new Date(guidelines.training_last_run_at).toLocaleString()} · ${
                  guidelines.training_last_exemplars_count ?? 0
                } exemplars`
              : "Never trained"}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <button
            type="button"
            onClick={onTrainClick}
            disabled={isTraining || trainableAssetCount < 2}
            className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-2 px-6 rounded-lg"
          >
            {isTraining ? "Training\u2026" : "Train brand"}
          </button>
          {trainableAssetCount < 2 && (
            <p className="text-xs text-gray-500 max-w-[280px] text-right">
              Upload at least 2 reference assets (hero ads, lighting refs, product shots, or mannequins) to enable training.
              Currently: {trainableAssetCount}.
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left column — visual attributes */}
        <div className="space-y-6">
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <label className="text-sm font-medium text-gray-300">Brand Colors</label>
              <button
                onClick={addColor}
                className="bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm"
              >
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {guidelines.colors.map((color, index) => (
                <div key={index} className="flex items-center gap-2 bg-gray-900 border border-gray-700 rounded-lg p-2">
                  <input
                    type="color"
                    value={color || "#000000"}
                    onChange={(e) => updateColor(index, e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer border-0"
                  />
                  <input
                    type="text"
                    value={color}
                    onChange={(e) => updateColor(index, e.target.value)}
                    className="flex-grow bg-transparent text-gray-200 text-sm focus:outline-none"
                    placeholder="#000000"
                  />
                  <button
                    onClick={() => removeColor(index)}
                    className="text-red-400 hover:text-red-300 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            {pendingTraining?.patch?.suggested_colors_to_add && pendingTraining.patch.suggested_colors_to_add.length > 0 && !dismissed.has("colors_to_add") && (
              <TrainingSuggestionCard<string>
                label="Suggested colors to add"
                items={pendingTraining.patch.suggested_colors_to_add}
                renderItem={(hex, isSel) => (
                  <div className="flex items-center gap-2 px-2 py-1">
                    <span className="w-5 h-5 rounded" style={{ background: hex }} />
                    <span className="font-mono text-xs text-gray-200">{hex}</span>
                    {isSel && <span className="text-purple-300">{"\u2713"}</span>}
                  </div>
                )}
                onAcceptSelected={(items) => {
                  onUpdate({ colors: Array.from(new Set([...guidelines.colors, ...items])) });
                  setDismissed((d) => new Set(d).add("colors_to_add"));
                }}
                onReject={() => setDismissed((d) => new Set(d).add("colors_to_add"))}
              />
            )}
          </div>

          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
            <label className="block text-sm font-medium text-gray-300 mb-3">Primary Theme Color</label>
            <div className="flex items-center gap-4">
              <input
                type="color"
                value={guidelines.theme_color || "#000000"}
                onChange={(e) => onUpdate({ theme_color: e.target.value })}
                className="w-16 h-16 rounded cursor-pointer"
              />
              <span className="text-gray-400">{guidelines.theme_color || "Not set"}</span>
            </div>
          </div>

          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <label className="text-sm font-medium text-gray-300">Brand Fonts</label>
              <button
                onClick={addFont}
                className="bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm"
              >
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
            <div className="space-y-3">
              {guidelines.fonts.map((font, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={font}
                    onChange={(e) => updateFont(index, e.target.value)}
                    className="flex-grow bg-gray-900 border border-gray-700 text-gray-200 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    placeholder="Font name (e.g., Inter)"
                  />
                  <button
                    onClick={() => removeFont(index)}
                    className="text-red-400 hover:text-red-300 transition-colors p-2"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
            {pendingTraining?.patch?.suggested_fonts_to_add && pendingTraining.patch.suggested_fonts_to_add.length > 0 && !dismissed.has("fonts_to_add") && (
              <TrainingSuggestionCard<TrainingPatchFont>
                label="Suggested fonts to add"
                items={pendingTraining.patch.suggested_fonts_to_add}
                renderItem={(font, isSel) => (
                  <div className="flex items-center gap-2 px-3 py-1">
                    <span className="text-sm text-gray-200">{font.family}</span>
                    {isSel && <span className="text-purple-300">{"\u2713"}</span>}
                  </div>
                )}
                onAcceptSelected={(items) => {
                  const existingFamilies = new Set(guidelines.fonts);
                  const newFonts = items.map((f) => f.family).filter((fam) => !existingFamilies.has(fam));
                  if (newFonts.length) onUpdate({ fonts: [...guidelines.fonts, ...newFonts] });
                  setDismissed((d) => new Set(d).add("fonts_to_add"));
                }}
                onReject={() => setDismissed((d) => new Set(d).add("fonts_to_add"))}
              />
            )}
          </div>
        </div>

        {/* Right column — structured textual rules */}
        <div className="space-y-6">
          {STRUCTURED_FIELDS.map(({ key, label, placeholder }) => (
            <div key={key} className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
              <label className="block text-sm font-medium text-gray-300 mb-3">{label}</label>
              <textarea
                value={(guidelines[key] as string) ?? ""}
                onChange={(e) => onUpdate({ [key]: e.target.value } as Partial<BrandGuidelines>)}
                rows={3}
                className="w-full bg-gray-900 border border-gray-700 text-gray-200 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:outline-none resize-y"
                placeholder={placeholder}
              />
              {pendingTraining?.patch?.[key] && !dismissed.has(key) && (
                <TrainingReviewCard
                  fieldLabel={label}
                  currentValue={(guidelines[key] as string) ?? ""}
                  proposedValue={pendingTraining.patch[key] as string}
                  justification={pendingTraining.justifications?.[key] ?? ""}
                  onAccept={(v) => { onUpdate({ [key]: v } as Partial<BrandGuidelines>); setDismissed((d) => new Set(d).add(key)); }}
                  onEdit={(v) => { onUpdate({ [key]: v } as Partial<BrandGuidelines>); setDismissed((d) => new Set(d).add(key)); }}
                  onReject={() => setDismissed((d) => new Set(d).add(key))}
                />
              )}
            </div>
          ))}

          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <label className="text-sm font-medium text-gray-300">Do NOT list</label>
              <button
                onClick={addDoNot}
                className="bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm"
              >
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
            <div className="space-y-2">
              {doNotList.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => updateDoNot(index, e.target.value)}
                    className="flex-grow bg-gray-900 border border-gray-700 text-gray-200 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    placeholder="e.g. no neon colors"
                  />
                  <button
                    onClick={() => removeDoNot(index)}
                    className="text-red-400 hover:text-red-300 transition-colors p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {doNotList.length === 0 && (
                <div className="text-gray-500 text-sm">No items.</div>
              )}
            </div>
            {pendingTraining?.patch?.do_not_list && pendingTraining.patch.do_not_list.length > 0 && !dismissed.has("do_not_list") && (
              <TrainingReviewCard
                fieldLabel="Do NOT list"
                currentValue={(guidelines.do_not_list ?? []).join("\n")}
                proposedValue={pendingTraining.patch.do_not_list.join("\n")}
                justification={pendingTraining.justifications?.do_not_list ?? ""}
                onAccept={(v) => { onUpdate({ do_not_list: v.split("\n").filter(Boolean) }); setDismissed((d) => new Set(d).add("do_not_list")); }}
                onEdit={(v) => { onUpdate({ do_not_list: v.split("\n").filter(Boolean) }); setDismissed((d) => new Set(d).add("do_not_list")); }}
                onReject={() => setDismissed((d) => new Set(d).add("do_not_list"))}
              />
            )}
          </div>

          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <Type className="w-4 h-4 text-gray-400" />
                <label className="text-sm font-medium text-gray-300">Text content conventions</label>
              </div>
              {textConv ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className={`inline-block w-2 h-2 rounded-full ${textConv.ads_typically_contain_text ? "bg-green-400" : "bg-gray-500"}`} />
                    <span className="text-sm text-gray-300">
                      {textConv.ads_typically_contain_text ? "Ads typically contain text overlays" : "Ads do not typically contain text"}
                    </span>
                  </div>
                  {textConv.summary && (
                    <p className="text-sm text-gray-400">{textConv.summary}</p>
                  )}
                  {textConv.typical_text_elements && textConv.typical_text_elements.length > 0 && (
                    <div className="space-y-1.5">
                      {textConv.typical_text_elements.map((el, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm">
                          <span className="text-gray-500 mt-0.5">-</span>
                          <div>
                            <span className="text-gray-200 font-medium">{el.role}</span>
                            <span className="text-gray-500"> ({el.placement})</span>
                            {el.description && <span className="text-gray-400">: {el.description}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">Not set — run training to detect text patterns from hero ads.</p>
              )}
              {pendingTraining?.patch?.text_content_conventions && !dismissed.has("text_content_conventions") && (
                <TrainingReviewCard
                  fieldLabel="Text content conventions"
                  currentValue={currentTextConvFormatted}
                  proposedValue={formatTextConventions(pendingTraining.patch.text_content_conventions)}
                  justification={pendingTraining.justifications?.text_content_conventions ?? ""}
                  onAccept={() => {
                    onUpdate({ text_content_conventions: pendingTraining.patch.text_content_conventions });
                    setDismissed((d) => new Set(d).add("text_content_conventions"));
                  }}
                  onEdit={(v) => {
                    const proposed = pendingTraining.patch.text_content_conventions!;
                    onUpdate({ text_content_conventions: parseTextConventions(v, proposed) });
                    setDismissed((d) => new Set(d).add("text_content_conventions"));
                  }}
                  onReject={() => setDismissed((d) => new Set(d).add("text_content_conventions"))}
                />
              )}
          </div>

          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
            <label className="text-sm font-medium text-gray-300 mb-4 block">Reference Sources</label>
            <div className="space-y-3">
              {guidelines.sources.map((source, index) => (
                <input
                  key={index}
                  type="url"
                  disabled
                  value={source}
                  className="w-full bg-gray-900 border border-gray-700 text-gray-400 rounded-lg p-3"
                />
              ))}
              {guidelines.sources.length === 0 && (
                <div className="text-gray-500 text-sm">No sources recorded.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {pendingTraining && pendingTraining.skipped_fields.length > 0 && (
        <div className="p-3 bg-gray-800/40 border border-gray-700 rounded-lg text-sm text-gray-400">
          <div className="font-semibold text-gray-300 mb-1">Skipped fields:</div>
          <ul className="list-disc pl-5 space-y-0.5">
            {pendingTraining.skipped_fields.map((s) => (
              <li key={s.field}>
                <span className="font-mono text-gray-300">{s.field}</span> — {s.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {pendingTraining && (
        <div className="flex items-center gap-3 justify-end">
          <button
            type="button"
            onClick={onDiscardTraining}
            className="text-gray-400 hover:text-gray-200 font-semibold py-2 px-4"
          >
            Discard all suggestions
          </button>
        </div>
      )}

      <div className="flex justify-end h-6">
        {saveStatus === "saving" && (
          <span className="flex items-center gap-1.5 text-sm text-gray-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Saving...
          </span>
        )}
        {saveStatus === "saved" && (
          <span className="flex items-center gap-1.5 text-sm text-green-400">
            <Check className="w-4 h-4" /> Saved
          </span>
        )}
      </div>
    </div>
  );
};

export default IdentityTab;
